import { subDomain } from '@octadesk-tech/services'
import Storage from '@octadesk-tech/storage'
import { flux as Flux } from '@octadesk/chat-domain'

import { inIframe, userAgent } from '@/helpers'
import services from '@/services'
import { emitAppSession, getExternalSessionKey } from '@/services/iframe'
import { verifyCancelCloseRoom } from '@/app/conversation/services/chat'

import * as types from './mutations-types'

export const setSessionPages = context => {
  const pages = services.session.getSessionPages()

  context.commit(types.SET_PAGES, pages)
}

export const setPage = async (context, url) => {
  if (context.getters.page.url) {
    context.getters.page.leaveDate = new Date()

    context.commit(types.ADD_PAGE, context.getters.page)
  }

  context.commit(types.SET_PAGE, url)

  await context.dispatch('chooseFlux')
}

export const setSessionAppActive = context => {
  if (context.getters.options.reopen !== 'false') {
    const active = services.session.getSessionActive()

    if (active) {
      context.dispatch('activeApp')
    } else {
      context.dispatch('inactiveApp')
    }
  }
}

function removeOlderMessages({ dispatch, getters }) {
  services.session.clearSessionMessages()

  if (getters.chat && getters.chat.key) {
    dispatch('updateMessages')
  } else {
    dispatch('setMessages', [])
  }
}

async function restoreRoomSession(context, roomKey) {
  try {
    let token = Storage.getItem(`roomToken-${roomKey}`)
    let room = Storage.getItem(`room-${roomKey}`)

    const lastModified = await services.room.getLastUpdate(roomKey)

    const outdatedChat = () => {
      if (!lastModified) return true

      const hasRoomUpdatedAt =
        room && Object.prototype.hasOwnProperty.call(room, 'updatedAt')

      if (hasRoomUpdatedAt) {
        return new Date(lastModified) > new Date(room.updatedAt)
      }

      return true
    }

    if (!token || !room || outdatedChat()) {
      const data = await services.room.getRoom(roomKey)

      token = data.token

      room = data.room
    }

    if (token) {
      context.dispatch('setWidgetRoomToken', { token, roomKey })
    }

    if (room && [0, 1, 4].indexOf(room.status) !== -1) {
      await context.dispatch('login', {
        user: room.createdBy,
        customFields: room.customFields
      })

      context.dispatch('setChat', room)

      if ([0, 1].includes(room.status)) {
        context.dispatch('connect')
      }

      removeOlderMessages(context)
    } else {
      context.dispatch('clearSession')

      context.dispatch('startChooseFluxInterval')

      context.dispatch('stopRestoring')
    }
  } catch (ex) {
    console.error('restoreRoomSession', ex)

    context.dispatch('clearSession')

    context.dispatch('stopRestoring')
  }
}

export const stopRestoring = context => {
  context.commit(types.SET_RESTORING, false)
}

export const delayedStopRestoring = context => {
  setTimeout(() => {
    const { restoringSession } = context.getters
    if (restoringSession) {
      context.dispatch('stopRestoring')
    }
  }, 1000)
}

export const setSessionRoom = async context => {
  let roomKey = services.session.getSessionChatKey()
  if (!roomKey && userAgent.isSafari()) {
    const { options } = context.getters

    if (options && options.session) {
      roomKey = options.session
    }
  }

  if (roomKey && roomKey.length) {
    const validate = await verifyCancelCloseRoom(roomKey)
    if (validate && validate.data !== 'false') {
      await restoreRoomSession(context, roomKey)
      return
    }
  }
  context.dispatch('startChooseFluxInterval')
}

export const setSessionUserId = context => {
  const userId = services.session.getUserId()

  if (userId) {
    context.dispatch('setUserId', userId)

    context.dispatch('findCustomer', userId)
  }
}

export const restoreSession = async context => {
  await context.dispatch('setSessionUserId')

  await context.dispatch('setSessionAppActive')

  await context.dispatch('setSessionPages')

  await context.dispatch('setSessionRoom')
}

export const clearSessionPages = context => {
  services.session.clearSessionPages()

  context.dispatch('setSessionPages')
}

export const setSessionChatKey = () => services.session.setSessionChatKey()

export const clearSessionMessages = () =>
  services.session.clearSessionMessages()

export const setSessionActive = () => {
  services.session.setSessionActive()
}

export const clearSession = () => services.session.clearSession()

export const externalLogin = async (context, payload) => {
  const { restoringSession } = context.getters

  if (restoringSession) {
    window.setTimeout(() => {
      context.dispatch('externalLogin', payload)
    }, 500)

    return
  }

  const externalUser = payload.user ? payload.user : payload

  const user = {
    email: externalUser.email,
    name: externalUser.name,
    thumbUrl: externalUser.thumbUrl
  }

  const customFields = payload.customFields || []

  const organization = payload.organization

  context.commit(types.SET_EXTERNAL_LOGIN)

  await context.dispatch('clearCachedUser')

  context.dispatch('overrideSetCustomFields', customFields)

  await context.dispatch('setUser', user)

  if (organization) {
    await context.dispatch('setOrganization', organization)
  }
}

export const setScrollPercent = (context, scrollPercent) => {
  context.commit(types.SET_SCROLL_PERCENT, scrollPercent)

  context.dispatch('chooseFlux')
}

let choosenFlux = false

export const chooseFlux = async context => {
  const {
    onConversation,
    restoringSession,
    session,
    fluxId,
    selectedStorie,
    company,
    isWozTestAgent
  } = context.getters

  if (!company || !company.length) {
    return
  }
  if (!choosenFlux && !onConversation) {
    choosenFlux = true

    const clonedSession = JSON.parse(JSON.stringify(session))

    clonedSession.page = clonedSession.page || {}

    clonedSession.page.leaveDate = new Date()

    const fluxes = await context.dispatch('getFluxes')

    const storiesList = fluxes.filter(
      f =>
        f.stories &&
        f.stories.enabled &&
        f.stories.medias &&
        f.stories.medias.length &&
        f.valid(clonedSession)
    )

    if (storiesList && storiesList.length && !selectedStorie) {
      context.dispatch('setStoriesList', storiesList)

      if (!inIframe() || context.getters.parentSize) {
        context.dispatch('setShowStories', true)
      }
    } else {
      const WOZ_BOT_TEST_PROFILE_NAME = 'WOZ-BOT-TEST-AGENT'

      const webFluxes = fluxes.filter(
        f =>
          (!f.channel || f.channel === 'web') &&
          f.name !== WOZ_BOT_TEST_PROFILE_NAME
      )

      let flux =
        (selectedStorie &&
          webFluxes.find(f => f.id === selectedStorie.fluxId)) ||
        webFluxes.find(f => f.botId && f.valid(clonedSession)) ||
        webFluxes.find(f => !f.botId && f.valid(clonedSession))

      if (isWozTestAgent) {
        flux = fluxes.find(f => f.name === WOZ_BOT_TEST_PROFILE_NAME)
      }

      if (flux && flux.id !== fluxId) {
        await context.dispatch('verifyFormRequired', flux)

        if (!restoringSession) {
          services.flux.fluxStatistics(flux.id).sended()
        }

        services.iframe.postMessage({
          name: 'onChooseFlux',
          flux
        })
      }

      await context.dispatch('setFlux', flux)
    }

    choosenFlux = false
  }
}

export const setGuestUser = async context => {
  const { getNewGuestUser, user } = context.getters

  if (!user || user.guest) {
    const guest = getNewGuestUser()

    await context.dispatch('upsertUser', guest)
  }
}

export const verifyFormRequired = async (context, flux) => {
  if (flux && flux.botId) return

  const { externalLogin, onConversation, getNewGuestUser, user } =
    context.getters

  const hasValidUser = user && user.email && user.name && !user.guest

  if (!externalLogin && !hasValidUser) {
    if (flux.formRequired && !onConversation) {
      context.dispatch('setCustomFields', [])

      context.dispatch('setUser', {})

      context.dispatch('goToConversation')
    } else {
      const guestUser = getNewGuestUser()

      context.dispatch('login', {
        user: guestUser
      })
    }
  }
}

export const setFlux = async (context, flux) => {
  const previousFluxFormId = context.getters.fluxFormId
    ? context.getters.fluxFormId.toString()
    : ''

  context.commit(types.SET_FLUX, flux)

  if (
    previousFluxFormId !== context.getters.fluxFormId &&
    flux &&
    flux.formRequired
  ) {
    context.dispatch('fetchForm', context.getters.fluxFormId)
  }

  if (flux && (flux.stories || flux.message)) {
    await context.dispatch('handleProactiveMessages')
  }

  await context.dispatch('startFluxBot')
}

export const getFlux = async (context, fluxId) => {
  const flux = await services.flux.getFlux(fluxId)

  const sessionKey = getExternalSessionKey('fluxId')
  if (sessionKey) {
    emitAppSession({ [sessionKey]: fluxId })
  }

  context.dispatch('setFlux', flux)
}

let chooseFluxInterval

export const getFluxes = async () => {
  let fluxes = services.session.getFluxes()

  if (!fluxes) {
    const sub = subDomain.getSubDomain()
    if (!sub || !sub.length) {
      console.debug('subdomain not resolved yet, retrying in 1 second')
      return new Promise(resolve =>
        setTimeout(() => getFluxes().then(resolve), 1000)
      )
    }

    fluxes = await services.flux.getFluxes()

    services.session.setFluxes(fluxes)
  }

  return (fluxes || []).map(f => new Flux(f))
}

export const getMinChooseFluxInterval = async context => {
  const fluxes = await context.dispatch('getFluxes')

  const conditionsGroups = fluxes.map(f => {
    return f.conditions.filter(c => {
      return c.field.name === 'secondsOnPageSession'
    })
  })

  const timeConditions = conditionsGroups.reduce(
    (aggregate, current) => [...aggregate, ...current],
    []
  )

  timeConditions.sort((a, b) => {
    if (a.value < b.value) {
      return -1
    }

    if (a.value > b.value) {
      return 1
    }

    return 0
  })

  return timeConditions.length ? 1000 * timeConditions[0].value : null
}

export const startChooseFluxInterval = async context => {
  const intervalTime = await context.dispatch('getMinChooseFluxInterval')

  const handler = async context => {
    if (context.getters.onConversation && chooseFluxInterval) {
      clearInterval(chooseFluxInterval)
    } else {
      await context.dispatch('chooseFlux')
    }
  }

  await handler(context)

  if (intervalTime) {
    chooseFluxInterval = setInterval(() => handler(context), intervalTime)
  }
}

export const openProactiveMsg = (context, fluxId) =>
  services.flux.fluxStatistics(fluxId).opened()

export const setExistentContact = (context, existent) =>
  context.commit(types.SET_EXISTENT_CONTACT, existent)

export const setSurveyFinished = ({ commit }, isSurveyFinished) =>
  commit(types.SET_SURVEY_FINISHED, isSurveyFinished)
